/**
 * AssigneeBadges — 현장 카드·목록의 '담당자 [이름]' · '관리자 [이름]' (2026-10-12 개선사항 D.0).
 *  - 2명 이상이면 [이름 외 N]. 대표 이름 = 조회 조건에 입력한 직원 → 나 → 지정 순서상 첫 번째
 *  - 조회 조건과 일치한 배지만 강조 → 담당자로 조회됐는지 관리자로 조회됐는지 구분
 *  - [이름 외 N ▾] 클릭 시 전체 명단 팝오버(지정 순서, 조건 일치 직원 강조, 머리글 없음).
 *    바깥 클릭 · Esc 로 닫힘. 1명이면 이름만(클릭 없음). 배지 클릭은 카드 클릭과 별개(버블링 차단)
 *  - 모양은 추출기획서(20261012) 기준: 역할 라벨은 회색 글자로 알약 밖, 알약 안에는 이름만.
 *    emptyText 를 주면 비어 있는 역할도 '담당자 미지정' · '관리자 미지정'으로 표시한다(스케줄러·공사현황).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Tag } from '@indipia/ui-core';
import type { AssignedMember } from './types';

export interface AssigneeBadgesProps {
  chargeMembers?: AssignedMember[] | null;
  managerMembers?: AssignedMember[] | null;
  /** 현재 담당자·관리자 조회 조건(선택 직원) */
  filterIds?: number[];
  myId?: number | null;
  /** 비어 있는 역할에 보일 문구(예: '미지정'). null 이면 비어 있는 역할은 그리지 않음 */
  emptyText?: string | null;
  /** 명단 팝오버에서 본인 옆에 '(나)' 표시(스케줄러·공사현황 기획) */
  popoverMe?: boolean;
  /** 줄 바꿈 허용(카드 폭을 넘으면 관리자는 다음 줄) */
  wrap?: boolean;
  className?: string;
}

function displayName(m: AssignedMember): string {
  return m.memberName ?? '(이름 없음)';
}

/** 대표 이름 순서: 조건 일치(조건에 입력한 순서) → 나 → 지정 순서상 첫 번째 */
export function pickRepresentative(
  members: AssignedMember[], filterIds: number[] = [], myId?: number | null,
): AssignedMember | undefined {
  const byFilter = filterIds.map((id) => members.find((m) => m.memberId === id)).find(Boolean);
  return byFilter
    ?? (myId != null ? members.find((m) => m.memberId === myId) : undefined)
    ?? members[0];
}

/** '담당 ○○ 외 N · 관리 ○○' 한 줄 문구(셀렉박스 목록 둘째 줄용, D-8 · D-10 · D-11) */
export function assigneeSummaryText(
  chargeMembers?: AssignedMember[] | null, managerMembers?: AssignedMember[] | null,
  filterIds: number[] = [], myId?: number | null,
): string {
  const part = (prefix: string, list?: AssignedMember[] | null) => {
    if (!list?.length) return null;
    const rep = pickRepresentative(list, filterIds, myId)!;
    return `${prefix} ${displayName(rep)}${list.length > 1 ? ` 외 ${list.length - 1}` : ''}`;
  };
  const charge = part('담당', chargeMembers);
  const manager = part('관리', managerMembers);
  if (!charge && !manager) return '담당자 미지정';
  return [charge, manager].filter(Boolean).join(' · ');
}

function RoleBadge({ role, members, filterIds, myId, popoverMe }: {
  role: '담당자' | '관리자';
  members: AssignedMember[];
  filterIds: number[];
  myId?: number | null;
  popoverMe?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // 카드가 overflow:hidden 이거나 스크롤 목록 안이어도 잘리지 않게 body 에 fixed 로 띄운다
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLSpanElement>(null);
  const rep = pickRepresentative(members, filterIds, myId)!;
  const matched = filterIds.length > 0 && members.some((m) => filterIds.includes(m.memberId));
  const many = members.length > 1;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // 스크롤·리사이즈되면 위치가 어긋나므로 닫는다
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  // 알약 = ui-core Tag(D-09 §15.5 뉴트럴 gray.100/300/600 — 기획의 회색 알약과 같은 규격).
  // 조회 조건과 일치하면 groupTheme 로 강조 — 색만으로 전달하지 않도록 굵기도 함께 바꾼다(D-07 §3.2 #5)
  const pill = (
    <Tag
      className={`max-w-full gap-02 ${matched ? '!border-group !bg-group-subtle font-semibold !text-group-onSubtle' : ''}`}
    >
      <span className="truncate">{displayName(rep)}{many ? ` 외 ${members.length - 1}` : ''}</span>
      {many && <span aria-hidden="true">{open ? '▴' : '▾'}</span>}
    </Tag>
  );

  return (
    <span className="inline-flex min-w-0 items-center gap-02">
      <span className="shrink-0 text-caption text-text-muted">{role}</span>
      {!many ? (
        // 1명 — 이름만(클릭 없음). 그래도 카드 클릭으로 번지지 않게 막는다
        // (mousedown 도 막는다 — 카드의 드래그 선택 등이 배지에서 시작되지 않게)
        <span className="inline-flex min-w-0" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>{pill}</span>
      ) : (
        <span ref={rootRef} className="relative inline-flex min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-haspopup="true"
            aria-expanded={open}
            className="!h-auto !min-h-0 !p-0"
            onMouseDown={(e) => e.stopPropagation()}
            // 카드·머리글의 Enter/Space 이동 핸들러까지 올라가지 않게(버블링 차단)
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              const r = e.currentTarget.getBoundingClientRect();
              setPos({ top: r.bottom + 4, left: r.left });
              setOpen((v) => !v);
            }}
          >
            {pill}
          </Button>
          {open && pos && createPortal(
            <span
              ref={popRef}
              role="dialog"
              aria-label={`${role} 전체 명단`}
              className="fixed z-dropdown flex flex-col gap-02 rounded-md border border-border bg-surface-raised p-03 text-caption"
              style={{ top: pos.top, left: pos.left, boxShadow: 'var(--elevation-2)' }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {members.map((m) => {
                const hit = filterIds.includes(m.memberId);
                return (
                  <span key={m.memberId} className={hit ? 'font-semibold text-group' : 'text-text'}>
                    {displayName(m)}{popoverMe && myId != null && m.memberId === myId ? ' (나)' : ''}
                  </span>
                );
              })}
            </span>,
            document.body,
          )}
        </span>
      )}
    </span>
  );
}

function EmptyRole({ role, text }: { role: string; text: string }) {
  return (
    <span className="inline-flex items-center gap-02 text-caption text-text-muted">
      <span>{role}</span><span>{text}</span>
    </span>
  );
}

export function AssigneeBadges({
  chargeMembers, managerMembers, filterIds = [], myId, emptyText = null, popoverMe = false, wrap = true, className = '',
}: AssigneeBadgesProps) {
  const charge = chargeMembers ?? [];
  const manager = managerMembers ?? [];
  if (!emptyText && !charge.length && !manager.length) return null;
  return (
    <span className={`flex items-center gap-x-04 gap-y-02 ${wrap ? 'flex-wrap' : ''} ${className}`}>
      {charge.length > 0
        ? <RoleBadge role="담당자" members={charge} filterIds={filterIds} myId={myId} popoverMe={popoverMe} />
        : emptyText && <EmptyRole role="담당자" text={emptyText} />}
      {manager.length > 0
        ? <RoleBadge role="관리자" members={manager} filterIds={filterIds} myId={myId} popoverMe={popoverMe} />
        : emptyText && <EmptyRole role="관리자" text={emptyText} />}
    </span>
  );
}
